import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export function SimplePromptModal({ isOpen, title, defaultText, onConfirm, onCancel, description }: { isOpen: boolean, title: string, defaultText: string, onConfirm: (val: string) => void, onCancel: () => void, description?: string }) {
  const { t } = useTranslation();
  const [val, setVal] = useState(defaultText);
  useEffect(() => { setVal(defaultText) }, [defaultText, isOpen]);

  if (!isOpen) return null;
  return (
    <div 
      className="modal-container" 
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-zinc-200 font-medium">{title}</h3>
        {description && <p className="text-sm text-zinc-400">{description}</p>}
        <input 
          id="simple-prompt-input"
          name="prompt-input"
          aria-label={title || "Prompt input"}
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          onFocus={(e) => e.target.select()}
          className="bg-[#0a0a0a] border border-zinc-800 text-sm p-2 rounded-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') onConfirm(val);
            if (e.key === 'Escape') onCancel();
          }}
        />
        <div className="flex justify-end gap-3 mt-2">
          <button onClick={onCancel} className="text-xs text-zinc-400 hover:text-zinc-300 px-2 py-1.5">{t("common.cancel")}</button>
          <button onClick={() => onConfirm(val)} className="text-xs bg-emerald-900/30 text-emerald-400 px-3 py-1.5 rounded-sm hover:bg-emerald-900/50 transition-colors">{t("common.confirm")}</button>
        </div>
      </div>
    </div>
  );
}

export function PortfolioPromptModal({ isOpen, title, defaultName, defaultType = 'REGULAR', onConfirm, onCancel, description }: { isOpen: boolean, title: string, defaultName: string, defaultType?: 'REGULAR' | 'WATCHLIST', onConfirm: (val: { name: string, type: 'REGULAR' | 'WATCHLIST' }) => void, onCancel: () => void, description?: string }) {
  const { t } = useTranslation();
  const [name, setName] = useState(defaultName);
  const [type, setType] = useState<'REGULAR' | 'WATCHLIST'>(defaultType);
  useEffect(() => { 
    setName(defaultName);
    setType(defaultType);
  }, [defaultName, defaultType, isOpen]);

  if (!isOpen) return null;
  return (
    <div 
      className="modal-container" 
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-zinc-200 font-medium">{title}</h3>
        {description && <p className="text-sm text-zinc-400">{description}</p>}
        <div className="mt-4 flex flex-col gap-3">
          <div>
            <label htmlFor="portfolio-name-prompt-input" className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1">{t("common.name", { defaultValue: "Name" })}</label>
            <input 
              id="portfolio-name-prompt-input"
              name="portfolio-name"
              aria-label={t("common.name", { defaultValue: "Name" })}
              autoFocus
              autoComplete="off"
              onFocus={(e) => e.target.select()}
              className="w-full bg-[#0a0a0a] border border-zinc-800 text-sm p-2 rounded-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') onConfirm({ name, type });
                if (e.key === 'Escape') onCancel();
              }}
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1">{t("common.type", { defaultValue: "Type" })}</label>
            <div className="flex bg-[#0a0a0a] border border-zinc-800 p-1 rounded-sm gap-1">
              <button 
                onClick={() => setType('REGULAR')}
                className={`flex-1 text-xs py-1.5 rounded-sm transition-colors ${type === 'REGULAR' ? 'bg-zinc-800 text-zinc-200' : 'text-zinc-400 hover:text-zinc-300'}`}
              >
                {t("portfolio.regular_type", { defaultValue: "Regular" })}
              </button>
              <button 
                onClick={() => setType('WATCHLIST')}
                className={`flex-1 text-xs py-1.5 rounded-sm transition-colors ${type === 'WATCHLIST' ? 'bg-zinc-800 text-zinc-200' : 'text-zinc-400 hover:text-zinc-300'}`}
              >
                {t("portfolio.watchlist_type", { defaultValue: "Watchlist" })}
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onCancel} className="text-xs text-zinc-400 hover:text-zinc-300 px-2 py-1.5">{t("common.cancel")}</button>
          <button onClick={() => onConfirm({ name, type })} className="text-xs bg-emerald-900/30 text-emerald-400 px-3 py-1.5 rounded-sm hover:bg-emerald-900/50 transition-colors">{t("common.confirm")}</button>
        </div>
      </div>
    </div>
  );
}

export function SimpleConfirmModal({ isOpen, title, message, onConfirm, onCancel, confirmText, isDanger = false, showCancel = true }: { isOpen: boolean, title: string, message: string, onConfirm: () => void, onCancel: () => void, confirmText?: string, isDanger?: boolean, showCancel?: boolean }) {
  const { t } = useTranslation();
  if (!isOpen) return null;
  
  const btnClass = isDanger 
    ? "bg-red-900/30 text-red-400 hover:bg-red-900/50" 
    : "bg-emerald-900/30 text-emerald-400 hover:bg-emerald-900/50";
    
  return (
    <div 
      className="modal-container" 
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-zinc-200 font-medium">{title}</h3>
        <p className="text-sm text-zinc-400">{message}</p>
        <div className="flex justify-end gap-3 mt-2">
          {showCancel && <button onClick={onCancel} className="text-xs text-zinc-400 hover:text-zinc-300 px-2 py-1.5">{t("common.cancel")}</button>}
          <button onClick={onConfirm} className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${btnClass}`}>{confirmText || t("common.confirm")}</button>
        </div>
      </div>
    </div>
  );
}

export type UnsavedChangesDecision = 'SAVE' | 'IGNORE' | 'CANCEL';

export function UnsavedChangesModal({
  isOpen,
  title,
  message,
  onSave,
  onIgnore,
  onCancel,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  onSave: () => void;
  onIgnore: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div
      className="modal-container"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-zinc-200 font-medium">{title}</h3>
        <p className="text-sm text-zinc-400 mt-1">{message}</p>
        <div className="flex justify-end gap-2 sm:gap-3 mt-4 flex-wrap">
          <button
            onClick={onCancel}
            className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-1.5 rounded-sm border border-zinc-800 hover:bg-zinc-800 transition-colors"
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            onClick={onIgnore}
            className="text-xs text-amber-400 hover:text-amber-300 px-3 py-1.5 rounded-sm bg-amber-900/20 hover:bg-amber-900/40 border border-amber-500/30 transition-colors font-medium"
          >
            {t("common.ignore", { defaultValue: "Ignore" })}
          </button>
          <button
            onClick={onSave}
            className="text-xs text-emerald-400 hover:text-emerald-300 px-3.5 py-1.5 rounded-sm bg-emerald-900/30 hover:bg-emerald-900/50 border border-emerald-500/30 transition-colors font-medium"
          >
            {t("common.save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useModals() {
  const { t } = useTranslation();
  const [promptState, setPromptState] = useState<{ isOpen: boolean, title: string, defaultText: string }>({ isOpen: false, title: '', defaultText: '' });
  const [portfolioPromptState, setPortfolioPromptState] = useState<{ isOpen: boolean, title: string, defaultName: string, defaultType: 'REGULAR' | 'WATCHLIST' }>({ isOpen: false, title: '', defaultName: '', defaultType: 'REGULAR' });
  const [confirmState, setConfirmState] = useState<{ isOpen: boolean, title: string, message: string, confirmText: string, isDanger: boolean, showCancel: boolean }>({ isOpen: false, title: '', message: '', confirmText: t("common.confirm"), isDanger: false, showCancel: true });
  const [unsavedState, setUnsavedState] = useState<{ isOpen: boolean, title: string, message: string }>({ isOpen: false, title: '', message: '' });

  const promptResolver = useRef<{ resolve: (val: string | null) => void } | null>(null);
  const portfolioPromptResolver = useRef<{ resolve: (val: { name: string, type: 'REGULAR' | 'WATCHLIST' } | null) => void } | null>(null);
  const confirmResolver = useRef<{ resolve: (val: boolean) => void } | null>(null);
  const unsavedResolver = useRef<{ resolve: (val: UnsavedChangesDecision) => void } | null>(null);

  const promptUser = (title: string, defaultText: string = '') => {
    setPromptState({ isOpen: true, title, defaultText });
    return new Promise<string | null>((resolve) => {
      promptResolver.current = { resolve };
    });
  };

  const promptPortfolio = (title: string, defaultName: string = '', defaultType: 'REGULAR' | 'WATCHLIST' = 'REGULAR') => {
    setPortfolioPromptState({ isOpen: true, title, defaultName, defaultType });
    return new Promise<{ name: string, type: 'REGULAR' | 'WATCHLIST' } | null>((resolve) => {
      portfolioPromptResolver.current = { resolve };
    });
  };

  const confirmUser = (title: string, message: string, options: { confirmText?: string, isDanger?: boolean, showCancel?: boolean } = {}) => {
    setConfirmState({ 
      isOpen: true, 
      title, 
      message, 
      confirmText: options.confirmText || t("common.confirm"), 
      isDanger: options.isDanger || false,
      showCancel: options.showCancel !== false
    });
    return new Promise<boolean>((resolve) => {
      confirmResolver.current = { resolve };
    });
  };
  
  const alertUser = (title: string, message: string) => {
    return confirmUser(title, message, { confirmText: t("common.ok"), showCancel: false });
  };

  const confirmUnsavedChanges = (title?: string, message?: string) => {
    setUnsavedState({
      isOpen: true,
      title: title || t("portfolio.unsaved_changes_title"),
      message: message || t("portfolio.unsaved_changes_message"),
    });
    return new Promise<UnsavedChangesDecision>((resolve) => {
      unsavedResolver.current = { resolve };
    });
  };

  const handlePromptConfirm = (val: string) => {
    setPromptState(p => ({ ...p, isOpen: false }));
    promptResolver.current?.resolve(val);
    promptResolver.current = null;
  };

  const handlePromptCancel = () => {
    setPromptState(p => ({ ...p, isOpen: false }));
    promptResolver.current?.resolve(null);
    promptResolver.current = null;
  };

  const handlePortfolioPromptConfirm = (val: { name: string, type: 'REGULAR' | 'WATCHLIST' }) => {
    setPortfolioPromptState(p => ({ ...p, isOpen: false }));
    portfolioPromptResolver.current?.resolve(val);
    portfolioPromptResolver.current = null;
  };

  const handlePortfolioPromptCancel = () => {
    setPortfolioPromptState(p => ({ ...p, isOpen: false }));
    portfolioPromptResolver.current?.resolve(null);
    portfolioPromptResolver.current = null;
  };

  const handleConfirmConfirm = () => {
    setConfirmState(p => ({ ...p, isOpen: false }));
    confirmResolver.current?.resolve(true);
    confirmResolver.current = null;
  };

  const handleConfirmCancel = () => {
    setConfirmState(p => ({ ...p, isOpen: false }));
    confirmResolver.current?.resolve(false);
    confirmResolver.current = null;
  };

  const handleUnsavedSave = () => {
    setUnsavedState(p => ({ ...p, isOpen: false }));
    unsavedResolver.current?.resolve('SAVE');
    unsavedResolver.current = null;
  };

  const handleUnsavedIgnore = () => {
    setUnsavedState(p => ({ ...p, isOpen: false }));
    unsavedResolver.current?.resolve('IGNORE');
    unsavedResolver.current = null;
  };

  const handleUnsavedCancel = () => {
    setUnsavedState(p => ({ ...p, isOpen: false }));
    unsavedResolver.current?.resolve('CANCEL');
    unsavedResolver.current = null;
  };

  const ModalComponents = (
    <>
      <SimplePromptModal 
        isOpen={promptState.isOpen} 
        title={promptState.title} 
        defaultText={promptState.defaultText} 
        onConfirm={handlePromptConfirm} 
        onCancel={handlePromptCancel} 
      />
      <PortfolioPromptModal 
        isOpen={portfolioPromptState.isOpen} 
        title={portfolioPromptState.title} 
        defaultName={portfolioPromptState.defaultName}
        defaultType={portfolioPromptState.defaultType}
        onConfirm={handlePortfolioPromptConfirm} 
        onCancel={handlePortfolioPromptCancel} 
      />
      <SimpleConfirmModal 
        isOpen={confirmState.isOpen} 
        title={confirmState.title} 
        message={confirmState.message} 
        confirmText={confirmState.confirmText}
        isDanger={confirmState.isDanger}
        showCancel={confirmState.showCancel}
        onConfirm={handleConfirmConfirm} 
        onCancel={handleConfirmCancel} 
      />
      <UnsavedChangesModal
        isOpen={unsavedState.isOpen}
        title={unsavedState.title}
        message={unsavedState.message}
        onSave={handleUnsavedSave}
        onIgnore={handleUnsavedIgnore}
        onCancel={handleUnsavedCancel}
      />
    </>
  );

  return { promptUser, promptPortfolio, confirmUser, alertUser, confirmUnsavedChanges, ModalComponents };
}
